"use client";

import React, { useCallback, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import * as MuiIcons from "@mui/icons-material";
import { Banner } from "@/types/contentful/banner";
import useDesktopBreakpoint from "@/hooks/general/use-desktop-breakpoint";
import useBannerHeight from "@/hooks/promotional-banner/use-banner-height";

interface PromotionalBannerClientProps {
  banner: Banner;
}

type MuiIconComponent = React.ComponentType<SvgIconProps>;

const ICON_PLACEHOLDER_PREFIX = "__ICON_";

const iconComponentMap: Map<string, MuiIconComponent> = (() => {
  const entries = Object.entries(MuiIcons) as Array<[string, MuiIconComponent]>;
  const map = new Map<string, MuiIconComponent>();
  entries.forEach(([name, component]) => {
    if (name === "__esModule") {
      return;
    }
    map.set(name.toLowerCase(), component);
  });
  return map;
})();

const defaultIconComponent = (() => {
  const info = iconComponentMap.get("info");
  if (info) {
    return info;
  }
  const iterator = iconComponentMap.values().next();
  return iterator.value ?? null;
})();

function toPascalCase(value: string): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const withoutSeparators = trimmed
    .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""));
  return withoutSeparators.charAt(0).toUpperCase() + withoutSeparators.slice(1);
}

function resolveIconComponent(rawName?: string): MuiIconComponent | null {
  if (!rawName) {
    return null;
  }

  const trimmed = rawName.trim();
  if (!trimmed) {
    return null;
  }

  const pascal = toPascalCase(trimmed);
  const candidates = Array.from(new Set<string>([
    trimmed,
    trimmed.replace(/\s+/g, ""),
    pascal,
    `${pascal}Icon`
  ]));

  for (let index = 0; index < candidates.length; index += 1) {
    const icon = iconComponentMap.get(candidates[index].toLowerCase());
    if (icon) {
      return icon;
    }
  }

  return null;
}

function parseInlineStyle(style?: string): Record<string, string> {
  if (!style) {
    return {};
  }

  return style
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, declaration) => {
      const [property, value] = declaration.split(":");
      if (!property || value === undefined) {
        return acc;
      }
      const trimmedProperty = property.trim();
      const trimmedValue = value.trim();
      const camelCaseProperty = trimmedProperty.replace(/-([a-z])/gi, (_, char: string) => char.toUpperCase());
      acc[trimmedProperty] = trimmedValue;
      acc[camelCaseProperty] = trimmedValue;
      return acc;
    }, {});
}

function renderMarkupWithIcons(markup: string, defaultColor: string): React.ReactNode[] {
  if (!markup) {
    return [];
  }

  const placeholders = new Map<string, { IconComponent: MuiIconComponent; style: Record<string, string> }>();
  let placeholderIndex = 0;

  const replacedMarkup = markup.replace(/<span[^>]*class=['"]icon['"][^>]*>(.*?)<\/span>/gi, (match, innerContent: string) => {
    const dataMuiMatch = match.match(/data-mui=['"]([^'"]+)['"]/i);
    const styleMatch = match.match(/style=['"]([^'"]+)['"]/i);
    const IconComponent = resolveIconComponent(dataMuiMatch?.[1]) ?? defaultIconComponent;

    if (!IconComponent) {
      return innerContent ?? "";
    }

    const token = `${ICON_PLACEHOLDER_PREFIX}${placeholderIndex}__`;
    placeholderIndex += 1;

    placeholders.set(token, {
      IconComponent,
      style: parseInlineStyle(styleMatch ? styleMatch[1] : undefined)
    });

    return token;
  });

  const sanitizedMarkup = replacedMarkup
    .replace(/<\/?p[^>]*>/gi, "")
    .replace(/<\/?div[^>]*>/gi, "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!sanitizedMarkup) {
    return [];
  }

  const segments = sanitizedMarkup
    .split(new RegExp(`(${ICON_PLACEHOLDER_PREFIX}[0-9]+__)`, "g"))
    .filter(Boolean);

  let textKeyCounter = 0;
  let iconKeyCounter = 0;

  return segments.map((segment) => {
    const placeholder = placeholders.get(segment);

    if (placeholder) {
      const { IconComponent, style } = placeholder;
      const fontSize = style.fontSize || style["font-size"];
      const color = style.color || style.fill || defaultColor;
      const key = `banner-icon-${iconKeyCounter}-${segment}`;
      iconKeyCounter += 1;

      const sxStyles: Record<string, unknown> = {
        color,
        verticalAlign: "middle",
        mx: 0.5,
        fontSize: fontSize || "inherit",
        display: "inline-flex",
        alignItems: "center",
        position: "relative",
        bottom: "1px"
      };

      return (
        <IconComponent
          key={key}
          sx={sxStyles}
        />
      );
    }

    const textKey = `banner-text-${textKeyCounter}`;
    textKeyCounter += 1;

    return (
      /* eslint-disable-next-line react/no-danger */
      <span
        key={textKey}
        dangerouslySetInnerHTML={{ __html: segment }}
      />
    );
  });
}

const PromotionalBannerClient: React.FC<PromotionalBannerClientProps> = ({
  banner
}) => {
  const theme = useTheme();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useDesktopBreakpoint();

  // Delegate all banner height management to custom hook
  useBannerHeight(bannerRef, isDesktop);

  // Use provided colors or fall back to theme defaults
  const backgroundColor = banner.bannerBackgroundColor || theme.palette.secondary.main;
  const textColor = banner.bannerTextColor || theme.palette.common.white;

  const bannerContentNodes = useMemo(
    () => renderMarkupWithIcons(banner.bannerText, textColor),
    [banner.bannerText, textColor]
  );

  // Process fine print field similarly
  const processedFinePrint = useMemo(() => {
    if (!banner.finePrintField) return "";

    let text = banner.finePrintField;
    text = text.replace(/<span[^>]*class=['"]icon['"][^>]*>.*?<\/span>/gi, "");
    text = text.replace(/<\/?p[^>]*>/gi, "");
    text = text.replace(/<\/?div[^>]*>/gi, "");
    text = text.replace(/<br\s*\/?>/gi, " ");
    text = text.replace(/\s+/g, " ");
    return text.trim();
  }, [banner.finePrintField]);

  const handleBannerClick = useCallback(() => {
    if (banner.bannerUrl) {
      window.location.href = banner.bannerUrl;
    }
  }, [banner.bannerUrl]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (banner.bannerUrl && (e.key === "Enter" || e.key === " ")) {
      handleBannerClick();
    }
  }, [banner.bannerUrl, handleBannerClick]);

  const renderBannerContent = useCallback((isSticky: boolean) => {
    const hasFinePrint = Boolean(processedFinePrint);

    return (
      <Box
        ref={isSticky ? bannerRef : undefined}
        sx={{
          position: "relative",
          zIndex: isSticky ? 999 : 1,
          width: "100%",
          backgroundColor,
          color: textColor,
          cursor: banner.bannerUrl ? "pointer" : "default",
          transition: "all 0.3s ease",
          minHeight: { xs: "auto", md: isSticky ? "38px" : "auto" },
          height: { xs: "auto", md: isSticky ? "38px" : "auto" },
          display: "flex",
          alignItems: "center",
          boxShadow: isSticky ? undefined : 1
        }}
        onClick={handleBannerClick}
        role={banner.bannerUrl ? "link" : "banner"}
        tabIndex={banner.bannerUrl ? 0 : undefined}
        onKeyDown={handleKeyDown}
        data-track="click"
        data-parent-container-type={isSticky ? "promotional-banner" : undefined}
        data-module-name="Promotional Banner"
        data-banner-id={banner.id}
        data-banner-rank={banner.bannerRank}
        data-banner-url={banner.bannerUrl}
      >
        <Box
          sx={{
            maxWidth: "1300px",
            margin: "0 auto",
            padding: {
              xs: theme.spacing(1.5, 2),
              sm: theme.spacing(1.5, 2.5),
              md: theme.spacing(0, 3)
            },
            width: "100%",
            minHeight: { xs: "auto", md: "38px" },
            height: { xs: "auto", md: isSticky ? "38px" : "auto" },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "center" },
            justifyContent: "center",
            gap: { xs: theme.spacing(1), md: theme.spacing(2) }
          }}
        >
          {/* Main banner content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: { xs: "0 0 auto", md: hasFinePrint ? "1 1 auto" : "0 0 auto" },
              textAlign: "center"
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "clamp(1.4rem, 2.5vw, 1.6rem)",
                fontWeight: 500,
                color: textColor,
                lineHeight: { xs: 1.35, sm: 1.3, md: 1 },
                whiteSpace: { xs: "normal !important", sm: "normal !important", md: "nowrap !important" },
                overflow: { xs: "visible", sm: "visible", md: "hidden" },
                textOverflow: { xs: "clip", sm: "clip", md: "ellipsis" },
                display: "inline !important",
                textAlign: "center",
                "& *": {
                  display: "inline !important",
                  whiteSpace: { xs: "normal !important", sm: "normal !important", md: "nowrap !important" },
                  margin: "0 !important",
                  padding: "0 !important"
                },
                "& strong": {
                  fontWeight: 700
                },
                "& a": {
                  color: textColor,
                  textDecoration: "underline",
                  "&:hover": {
                    textDecoration: "none"
                  }
                }
              }}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest("a")) {
                  e.stopPropagation();
                }
              }}
            >
              {bannerContentNodes}
            </Typography>
          </Box>

          {/* Fine print - centered below on mobile, vertically centered right on tablet/desktop */}
          {hasFinePrint && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-end" },
                flex: { xs: "0 0 auto", md: "0 0 auto" }
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "1.2rem", sm: "1.1rem", md: "1rem" },
                  fontWeight: { xs: 500, md: 500 },
                  color: textColor,
                  opacity: 0.95,
                  textAlign: { xs: "center", md: "right" },
                  maxWidth: { xs: "100%", md: "40ch" },
                  lineHeight: { xs: 1.3, md: 1.25 }
                }}
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: processedFinePrint }}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest("a")) {
                    e.stopPropagation();
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }, [banner, bannerContentNodes, processedFinePrint, backgroundColor, textColor, handleBannerClick, handleKeyDown, theme]);

  return renderBannerContent(isDesktop);
};

export default PromotionalBannerClient;
